import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styles from './ReferralBonusesPage.module.scss'; // Import SCSS module
// import Table from 'path/to/your/Table'; // Keep if you have a custom Table component
// import Button from 'path/to/your/Button'; // Keep if you have a custom Button component
// Import Redux actions
import {
    fetchAdminReferralBonuses,
    resetAdminReferralBonus,
    fetchAdminBonusResetHistory
} from '../../../redux/actions/referral/referralBonusesActions';
// Import regular action creator from the slice
import { clearAdminBonusResetError } from '../../../redux/slices/referral.slice';

const ReferralBonusesPage = () => {
    const dispatch = useDispatch();
    const [currentResettingExpertId, setCurrentResettingExpertId] = useState(null);

    // Select data from Redux store
    const {
        experts,
        loading,
        error,
        resettingBonus,
        resetError,
        resetHistory,
        resetHistoryLoading,
        resetHistoryError
    } = useSelector(state => state.referral.adminReferralBonuses);
    const profile = useSelector(state => state.user.userProfile.data);

    useEffect(() => {
        if (profile && profile.is_superuser) {
            dispatch(fetchAdminReferralBonuses());
            dispatch(fetchAdminBonusResetHistory());
        }
    }, [dispatch, profile]);

    // Effect to show alert on reset error and potentially clear it
    useEffect(() => {
        if (resetError) {
            // Using a more specific error message if available from the payload
            const message = (typeof resetError === 'object' && resetError?.message) 
                            ? resetError.message 
                            : (typeof resetError === 'string' ? resetError : 'Failed to reset bonuses');
            alert(`Error: ${message}`);
            setCurrentResettingExpertId(null);
            // dispatch(clearAdminBonusResetError()); // Uncomment to clear error from store after alert
        }
    }, [resetError, dispatch]);

    const handleResetBonuses = async (expertId) => {
        const reason = prompt("Please enter a reason for the reset (e.g., Monthly payout):");
        if (!reason) {
            return;
        }
        setCurrentResettingExpertId(expertId);
        dispatch(resetAdminReferralBonus({ expertId, reason })).then((action) => {
            if (resetAdminReferralBonus.fulfilled.match(action)) {
                alert('Bonuses reset successfully!');
                dispatch(fetchAdminReferralBonuses());
                dispatch(fetchAdminBonusResetHistory());
                setCurrentResettingExpertId(null);
            }
            // The resetError state change will trigger the useEffect above for alerts.
        });
    };

    if (!profile || !profile.is_superuser) {
        // console.log(profile); // Keep for debugging if needed
        return <div className={styles.errorContainer}>You are not authorized to view this page.</div>;
    }

    // Combined loading state for initial page load
    const initialLoading = loading && experts.length === 0 && resetHistoryLoading && resetHistory.length === 0;

    if (initialLoading) {
        return <div className={styles.loadingContainer}>Loading data...</div>;
    }

    const renderExpertsTable = () => {
        if (loading && experts.length === 0) {
            return <div className={styles.loadingContainer}>Loading expert bonuses...</div>;
        }
        if (error) {
            // Using a more specific error message if available from the payload
            const errorMessageText = (typeof error === 'object' && error?.message) 
                                   ? error.message 
                                   : (typeof error === 'string' ? error : 'Failed to fetch expert bonuses.');
            return <div className={`${styles.errorContainer} ${styles.errorMessage}`}>{errorMessageText}</div>;
        }
        if (!loading && experts.length === 0) {
            return <div className={styles.emptyContainer}>No expert bonuses found.</div>;
        }
        return (
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Email</th>
                            <th>Имя</th>
                            <th>Фамилия</th>
                            <th>Заработано</th>
                            <th>Ожидается</th>
                            <th>Выплачено</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {experts.map(expert => (
                            <tr key={expert.id}>
                                <td>{expert.id}</td>
                                <td>{expert.email}</td>
                                <td>{expert.first_name}</td>
                                <td>{expert.last_name}</td>
                                <td>{expert.total_bonuses_earned}</td>
                                <td>{expert.total_bonuses_pending}</td>
                                <td>{expert.total_bonuses_paid_out}</td>
                                <td>
                                    <button
                                        className={styles.button}
                                        onClick={() => handleResetBonuses(expert.id)}
                                        disabled={resettingBonus && currentResettingExpertId === expert.id}
                                    >
                                        {(resettingBonus && currentResettingExpertId === expert.id) ? 'Resetting...' : 'Reset Bonuses'}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderBonusResetHistoryTable = () => {
        if (resetHistoryLoading && resetHistory.length === 0) {
            return <div className={styles.loadingContainer}>Loading reset history...</div>;
        }
        if (resetHistoryError) {
            const errorMessageText = (typeof resetHistoryError === 'object' && resetHistoryError?.message)
                                   ? resetHistoryError.message
                                   : (typeof resetHistoryError === 'string' ? resetHistoryError : 'Failed to fetch bonus reset history.');
            return <div className={`${styles.errorContainer} ${styles.errorMessage}`}>{errorMessageText}</div>;
        }
        if (!resetHistoryLoading && resetHistory.length === 0) {
            return <div className={styles.emptyContainer}>No bonus reset history found.</div>;
        }
        return (
            <div className={styles.tableContainer} style={{ marginTop: '30px' }}> {/* Add some top margin */}
                <h2 className={styles.title} style={{ fontSize: '20px' }}>История сбросов бонусов</h2>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Email эксперта</th>
                            <th>Кем сброшено (Email)</th>
                            <th>Дата сброса</th>
                            <th>Причина</th>
                            <th>Expert ID</th>
                        </tr>
                    </thead>
                    <tbody>
                        {resetHistory.map(entry => (
                            <tr key={entry.id}>
                                <td>{entry.id}</td>
                                <td>{entry.expert_email}</td>
                                <td>{entry.reset_by_email}</td>
                                <td>{new Date(entry.reset_at).toLocaleString()}</td>
                                <td>{entry.reason}</td>
                                <td>{entry.expert}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>Бонусы рефералов</h1>
            {/* Display a general loading indicator if a fetch is happening after initial load (e.g. after reset) */}
            {loading && experts.length > 0 && <div className={styles.loadingContainer} style={{fontSize: '14px', padding: '10px 0'}}>Обновление данных экспертов...</div>}
            {/* Display reset error if any, using the new errorMessage class */}
            {resetError && !resettingBonus && (
                <div className={styles.errorMessage}>
                    Ошибка сброса бонуса: { (typeof resetError === 'object' && resetError?.message) 
                                                ? resetError.message 
                                                : (typeof resetError === 'string' ? resetError : 'Пожалуйста, попробуйте снова.')}
                    <button onClick={() => dispatch(clearAdminBonusResetError())} style={{marginLeft: '10px'}}>Закрыть</button>
                </div>
            )}
            {renderExpertsTable()}
            {renderBonusResetHistoryTable()}
        </div>
    );
};

export default ReferralBonusesPage; 